import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import {
  DealLinkResponse,
  DealLinkWriteRequest,
} from "src/dm/interfaces/DealLink";

const linkUrl = (linkId: number) => `${APIS.Links}/${linkId}`;
const linkForDealUrl = (dealId: number) =>
  `${APIS.Deals}/${dealId}/link`;
const linkMemberUrl = (linkId: number, dealId: number) =>
  `${APIS.Links}/${linkId}/deals/${dealId}`;

/** Fetch the link associated with a deal (DEAL.LNK_NO -> DEAL_LINK). */
export const useDealLink = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal-link", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<DealLinkResponse | null> => {
      try {
        const response = await apiClient.get<DealLinkResponse>(
          linkForDealUrl(dealId as number)
        );
        return response.data;
      } catch (err: unknown) {
        // 404 = the deal has no link yet — treat as null, not an error, so
        // the page can render an empty state instead of an error banner.
        if (
          typeof err === "object" &&
          err !== null &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
  });

/** Fetch a link by its ID. */
export const useLinkById = (linkId: number | undefined) =>
  useQuery({
    queryKey: ["link", linkId],
    enabled: linkId !== undefined && linkId > 0,
    queryFn: async (): Promise<DealLinkResponse> => {
      const response = await apiClient.get<DealLinkResponse>(
        linkUrl(linkId as number)
      );
      return response.data;
    },
  });

/** Update an existing link's editable fields. */
export const useUpdateDealLink = (linkId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealLinkWriteRequest
    ): Promise<DealLinkResponse> => {
      const response = await apiClient.put<DealLinkResponse>(
        linkUrl(linkId),
        body
      );
      return response.data;
    },
    onSuccess: (resp) => {
      qc.setQueryData(["link", linkId], resp);
      qc.invalidateQueries({ queryKey: ["deal-link"] });
    },
  });
};

/** Create a brand-new link header (no deals attached). */
export const useCreateDealLink = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DealLinkWriteRequest
    ): Promise<DealLinkResponse> => {
      const response = await apiClient.post<DealLinkResponse>(APIS.Links, body);
      return response.data;
    },
    onSuccess: (resp) => {
      qc.setQueryData(["link", resp.link.linkId], resp);
    },
  });
};

/** Attach an existing deal to an existing link. */
export const useAddDealToLink = (linkId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: number): Promise<DealLinkResponse> => {
      const response = await apiClient.post<DealLinkResponse>(
        linkMemberUrl(linkId, dealId)
      );
      return response.data;
    },
    onSuccess: (resp, dealId) => {
      qc.setQueryData(["link", linkId], resp);
      qc.invalidateQueries({ queryKey: ["deal-link", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};

/** Detach a deal from a link (sets DEAL.LNK_NO to null). */
export const useRemoveDealFromLink = (linkId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: number): Promise<void> => {
      await apiClient.delete(linkMemberUrl(linkId, dealId));
    },
    onSuccess: (_void, dealId) => {
      qc.invalidateQueries({ queryKey: ["link", linkId] });
      qc.invalidateQueries({ queryKey: ["deal-link", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};
